import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { t, getLang } from '../lib/i18n';
import { ProductCard } from './ProductResults';

const KEY = 'dealrun_assistant_session';
const sessionId = () => {
  let id = localStorage.getItem(KEY);
  if (!id) { id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem(KEY, id); }
  return id;
};
const SUGGESTIONS = ['Offres alimentaires cette semaine', 'Électronique à moins de 500 €', 'Offres parfums et beauté', 'Jouets pour enfants'];

const Bubble = ({ m, idx }) => (
  <div className={`flex flex-col gap-2 ${m.role === 'user' ? 'items-end' : 'items-start'}`} data-testid={m.provisional ? 'assistant-msg-provisional' : `assistant-msg-${m.role}-${idx}`}>
    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#E50914] text-white rounded-br-sm' : 'bg-[#262626] text-white rounded-bl-sm'}`}>
      {m.provisional && <Loader2 size={12} className="inline animate-spin me-1.5 -mt-0.5 text-[#E50914]" />}{m.content}
    </div>
    {m.products?.length > 0 && (
      <div className="w-full grid grid-cols-1 gap-2" data-testid={`assistant-products-${idx}`}>
        {m.products.map((p, i) => <ProductCard key={`${p.catalog_id}-${p.page}-${i}`} p={p} i={`${idx}-${i}`} testId="assistant-product" compact />)}
      </div>
    )}
  </div>
);

const AssistantChat = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    api.get(`/assistant/history/${sessionId()}`).then((r) => setMessages((m) => (m.length ? m : r.data.messages || []))).catch(() => {});
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading, open]);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onToggle = () => setOpen((o) => !o);
    window.addEventListener('dealgo:assistant', onToggle);
    return () => window.removeEventListener('dealgo:assistant', onToggle);
  }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput(''); setError('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setLoading(true);
    let settled = false;
    // Instant keyword hits appear while the AI refines; they are replaced by the final answer.
    api.get('/assistant/quick', { params: { q: msg } }).then((r) => {
      if (settled || !r.data.products?.length) return;
      setMessages((m) => [...m, { role: 'assistant', content: t('Voici ce que j\'ai trouvé immédiatement — l\'assistant affine…'), products: r.data.products, provisional: true }]);
    }).catch(() => {});
    try {
      const r = await api.post('/assistant/chat', { session_id: sessionId(), message: msg, lang: getLang() });
      settled = true;
      setMessages((m) => [...m.filter((x) => !x.provisional), { role: 'assistant', content: r.data.reply, products: r.data.products }]);
    } catch {
      settled = true;
      setMessages((m) => m.map((x) => (x.provisional ? { ...x, provisional: false, content: t('Voici ce que j\'ai trouvé immédiatement.') } : x)));
      setError(t('L\'assistant est actuellement indisponible. Veuillez réessayer.'));
    } finally { setLoading(false); }
  };

  const clear = () => { api.delete(`/assistant/history/${sessionId()}`).catch(() => {}); setMessages([]); };

  if (pathname.startsWith('/admin')) return null;
  return (
    <>
      <button onClick={() => setOpen((o) => !o)} aria-label={t('Assistant IA shopping')} data-testid="assistant-toggle"
        className={`hidden md:flex fixed bottom-5 end-5 z-[90] h-14 rounded-full bg-[#E50914] text-white shadow-[0_10px_30px_rgba(229,9,20,0.45)] flex items-center gap-2 px-4 font-bold text-sm transition-transform hover:scale-105 ${open ? 'scale-0 pointer-events-none' : ''}`}>
        <Sparkles size={20} /> <span className="hidden sm:inline">{t('Demander à l\'IA')}</span>
      </button>
      {open && (
        <div className="fixed z-[130] inset-x-0 bottom-0 sm:inset-auto sm:bottom-5 sm:end-5 sm:w-[400px] h-[85vh] sm:h-[620px] max-h-[calc(100vh-1rem)] flex flex-col bg-[#141414] border border-[#262626] sm:rounded-2xl shadow-2xl overflow-hidden" data-testid="assistant-panel">
          <div className="flex items-center justify-between px-4 h-14 border-b border-[#262626] bg-black">
            <div className="flex items-center gap-2 text-white">
              <span className="w-8 h-8 rounded-full bg-[#E50914] flex items-center justify-center"><MessageCircle size={16} /></span>
              <div className="leading-tight">
                <p className="text-sm font-bold" data-testid="assistant-title">{t('Assistant DealRun')}</p>
                <p className="text-[11px] text-[#808080]">{t('Trouve des produits dans tous les catalogues')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && <button onClick={clear} aria-label={t('Effacer la conversation')} data-testid="assistant-clear" className="w-9 h-9 rounded-full hover:bg-white/10 text-[#B3B3B3] flex items-center justify-center"><Trash2 size={16} /></button>}
              <button onClick={() => setOpen(false)} aria-label={t('Fermer')} data-testid="assistant-close" className="w-9 h-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="space-y-4" data-testid="assistant-welcome">
                <div className="bg-[#262626] text-white rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed max-w-[90%]">
                  {t('Bonjour ! Dites-moi ce que vous recherchez et je le trouverai dans les catalogues actuels de La Réunion. Essayez :')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} onClick={() => send(t(s))} data-testid={`assistant-suggestion-${i}`}
                      className="px-3 py-1.5 rounded-full border border-[#262626] bg-[#1a1a1a] text-xs font-semibold text-[#B3B3B3] hover:border-[#E50914] hover:text-white transition-colors">
                      {t(s)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} m={m} idx={i} />)}
            {loading && <div className="flex items-center gap-2 text-xs text-[#808080]" data-testid="assistant-loading"><Loader2 size={14} className="animate-spin" /> {t('Recherche dans les catalogues…')}</div>}
            {error && <p className="text-xs text-red-400" data-testid="assistant-error">{error}</p>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-[#262626] bg-black">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('ex : TV pas cher, dattes à moins de 20 €…')} maxLength={500} data-testid="assistant-input"
              className="flex-1 h-11 rounded-full bg-[#1a1a1a] border border-[#262626] px-4 text-sm text-white placeholder:text-[#808080] focus:outline-none focus:border-[#E50914]" />
            <button type="submit" disabled={!input.trim() || loading} aria-label={t('Envoyer')} data-testid="assistant-send"
              className="w-11 h-11 rounded-full bg-[#E50914] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#f6121d] transition-colors">
              <Send size={18} className="rtl:-scale-x-100" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
